import { QUESTIONS } from "./questions.js";

const SCORE = { min: 0, max: 10 };

const NA_VALUES = new Set(["N/A", "Not applicable"]);

export function normaliseAnswer(question, answerValue) {
  if (NA_VALUES.has(answerValue)) return null;

  const validOptions = Object.keys(question.answerAdvice).filter((o) => !NA_VALUES.has(o));
  const validIdx = validOptions.indexOf(answerValue);
  if (validIdx === -1) return null;

  if (question.polarity === "good-practice") {
    return 1 - validIdx / (validOptions.length - 1);
  }
  if (question.polarity === "symptom") {
    return answerValue === "Yes" ? 0 : 1;
  }
  if (question.polarity === "symptom-graded") {
    return validIdx / (validOptions.length - 1);
  }
  if (question.polarity === "architecture") {
    return answerValue === "Yes" ? 1 : 0;
  }
  return 0;
}

export function computeScore(answers) {
  let weightedSum = 0;
  let totalWeight = 0;
  const perQuestion = [];

  QUESTIONS.forEach((q, idx) => {
    const answer = answers[idx];
    const norm = normaliseAnswer(q, answer);

    perQuestion.push({
      idx,
      shortLabel: q.shortLabel,
      polarity: q.polarity,
      answer,
      normScore: norm,
      weight: q.weight,
    });

    if (norm !== null) {
      weightedSum += norm * q.weight;
      totalWeight += q.weight;
    }
  });

  const raw = totalWeight > 0 ? weightedSum / totalWeight : 0;
  const score = Math.round(raw * (SCORE.max - SCORE.min) * 10 + SCORE.min * 10) / 10;

  return { score, perQuestion };
}

export const CATEGORY_GROUPS = (() => {
  const map = new Map();
  QUESTIONS.forEach((q, idx) => {
    if (!q.group) return;
    if (!map.has(q.group)) map.set(q.group, []);
    map.get(q.group).push(idx);
  });
  return [...map.entries()].map(([label, questionIndices]) => ({ label, questionIndices }));
})();

export function computeGroupScores(perQuestion) {
  const byIdx = Object.fromEntries(perQuestion.map((q) => [q.idx, q]));
  return CATEGORY_GROUPS.map(({ label, questionIndices }) => {
    let weightedSum = 0;
    let totalWeight = 0;
    questionIndices.forEach((idx) => {
      const q = byIdx[idx];
      if (q && q.normScore !== null) {
        weightedSum += q.normScore * q.weight;
        totalWeight += q.weight;
      }
    });
    const norm = totalWeight > 0 ? weightedSum / totalWeight : null;
    const score = norm !== null ? Math.round(norm * 10) : null;
    return { label, score, norm };
  });
}

export function deriveTopTwo(perQuestion) {
  return perQuestion
    .filter((q) => q.normScore !== null && q.normScore < 0.5)
    .sort((a, b) => a.normScore - b.normScore || b.weight - a.weight)
    .slice(0, 2)
    .map((q) => ({ idx: q.idx, shortLabel: q.shortLabel, answer: q.answer }));
}
